import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback, useRef } from 'react';
import { ConfigProvider } from 'antd';
import {
    ThemeId,
    ThemeMetadata,
    getThemeById,
    getThemeMetadata,
    getAvailableThemes,
    getThemesByCategory,
    getDefaultTheme
} from './themeConfig';

interface ThemeContextType {
    currentThemeId: ThemeId;
    setTheme: (themeId: ThemeId) => void;
    availableThemes: ThemeMetadata[];
    getThemesByCategory: (category: ThemeMetadata['category']) => ThemeMetadata[];
    currentThemeMetadata: ThemeMetadata;
    nextTheme: () => void;
    previousTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
    children: ReactNode;
    defaultTheme?: ThemeId;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
    children,
    defaultTheme
}) => {
    const [currentThemeId, setCurrentThemeId] = useState<ThemeId>(defaultTheme || getDefaultTheme());

    // Utiliser des refs pour éviter les re-renders
    const availableThemesRef = useRef(getAvailableThemes());
    const availableThemes = availableThemesRef.current;

    // Charger le thème depuis le localStorage au montage
    useEffect(() => {
        const savedTheme = localStorage.getItem('theme-id') as ThemeId;
        if (savedTheme && availableThemes.some(theme => theme.id === savedTheme)) {
            setCurrentThemeId(savedTheme);
        } else {
            setCurrentThemeId(getDefaultTheme());
        }
    }, []); // Dépendances vides pour éviter les re-exécutions

    // Sauvegarder le thème dans le localStorage
    useEffect(() => {
        localStorage.setItem('theme-id', currentThemeId);
    }, [currentThemeId]);

    const setTheme = useCallback((themeId: ThemeId) => {
        if (themeId !== currentThemeId) {
            setCurrentThemeId(themeId);
        }
    }, [currentThemeId]);

    const nextTheme = useCallback(() => {
        const currentIndex = availableThemes.findIndex(theme => theme.id === currentThemeId);
        const nextIndex = (currentIndex + 1) % availableThemes.length;
        setCurrentThemeId(availableThemes[nextIndex].id);
    }, [availableThemes, currentThemeId]);

    const previousTheme = useCallback(() => {
        const currentIndex = availableThemes.findIndex(theme => theme.id === currentThemeId);
        const previousIndex = currentIndex === 0 ? availableThemes.length - 1 : currentIndex - 1;
        setCurrentThemeId(availableThemes[previousIndex].id);
    }, [availableThemes, currentThemeId]);

    const currentTheme = useMemo(() => getThemeById(currentThemeId), [currentThemeId]);
    const currentThemeMetadata = useMemo(() => getThemeMetadata(currentThemeId), [currentThemeId]);

    // Simplifier le contexte en évitant les dépendances circulaires
    const contextValue: ThemeContextType = useMemo(() => ({
        currentThemeId,
        setTheme,
        availableThemes,
        getThemesByCategory,
        currentThemeMetadata,
        nextTheme,
        previousTheme,
    }), [currentThemeId, setTheme, availableThemes, currentThemeMetadata, nextTheme, previousTheme]);

    return (
        <ThemeContext.Provider value={contextValue}>
            <ConfigProvider
                theme={{ ...currentTheme, cssVar: true }}
                componentSize="middle"
                direction="ltr"
            >
                {children}
            </ConfigProvider>
        </ThemeContext.Provider>
    );
};

// Hook pour utiliser le contexte de thème
export const useTheme = (): ThemeContextType => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

// Hook pour obtenir les tokens de thème actuels
export const useThemeToken = () => {
    const { currentThemeId } = useTheme();
    return getThemeById(currentThemeId).token;
};

// Hook pour obtenir les métadonnées du thème actuel
export const useThemeMetadata = (): ThemeMetadata => {
    const { currentThemeMetadata } = useTheme();
    return currentThemeMetadata;
};

export default ThemeProvider;
